import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Play, Pause, Music, Brain, Sparkles, Upload } from "lucide-react";
import ModeSelector from "./ModeSelector";

/**
 * AdvancedAudioBuilder — Bottom sheet picker for power users.
 *
 * Used in Private Suite ONLY (everywhere else uses the simple ModeSelector).
 * Exposes:
 *   • Mode (Clear / Soft / Deep)
 *   • Full music library, grouped by vibe, with 15s preview
 *   • Binaural picker (Beta / Alpha / Theta / Delta / Gamma / Off)
 *   • Optional "upload your own" music
 */

const VIBE_LABELS = {
  signature: "Signature",
  calm: "Calm",
  focus: "Focus",
  energising: "Energy",
  sleep: "Sleep",
  nature: "Nature",
  sacred: "Sacred",
  cinematic: "Cinematic",
  lofi: "Lo-Fi",
  workout: "Workout",
};

const PREVIEW_SECONDS = 15;

export default function AdvancedAudioBuilder({
  open,
  onClose,
  selection,         // { mode, musicId, binauralId }
  onChange,
  library,           // { tracks, vibes, binaural_presets }
}) {
  const [tab, setTab] = useState("music"); // music | binaural | mode
  const [previewing, setPreviewing] = useState(null);
  const audioRef = useRef(null);

  // Stop preview when sheet closes
  useEffect(() => {
    if (!open) {
      stopPreview();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const stopPreview = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    setPreviewing(null);
  };

  const startPreview = (track) => {
    stopPreview();
    const API = process.env.REACT_APP_BACKEND_URL || "";
    const url = track.url.startsWith("http") ? track.url : `${API}${track.url}`;
    audioRef.current = new Audio(url);
    audioRef.current.volume = 0.5;
    audioRef.current.play().catch(() => {});
    setPreviewing(track.id);
    setTimeout(() => {
      // Auto-stop after 15s
      if (audioRef.current && previewing === track.id) {
        stopPreview();
      } else if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
        setPreviewing((p) => (p === track.id ? null : p));
      }
    }, PREVIEW_SECONDS * 1000);
  };

  const togglePreview = (track) => {
    if (previewing === track.id) stopPreview();
    else startPreview(track);
  };

  // Group tracks by vibe
  const groupedTracks = (library?.tracks || []).reduce((acc, t) => {
    (acc[t.vibe] = acc[t.vibe] || []).push(t);
    return acc;
  }, {});

  // Order groups by the canonical vibe list from the API (signature first),
  // then any legacy/unknown vibes alphabetised at the end so nothing is lost.
  const vibeOrder = library?.vibes || Object.keys(VIBE_LABELS);
  const orderedVibeKeys = [
    ...vibeOrder.filter((v) => groupedTracks[v]?.length),
    ...Object.keys(groupedTracks)
      .filter((v) => !vibeOrder.includes(v))
      .sort(),
  ];

  const binauralPresets = library?.binaural_presets || {};

  if (!library) return null;

  return (
    <AnimatePresence>
      {open && (
        <>
          {/* Scrim */}
          <motion.div
            className="fixed inset-0 z-[120]"
            style={{ background: "rgba(0,0,0,0.55)" }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            data-testid="advanced-builder-scrim"
          />

          {/* Sheet */}
          <motion.div
            className="fixed left-0 right-0 bottom-0 z-[121] rounded-t-3xl"
            style={{
              background: "#0e0d0a",
              borderTop: "1px solid rgba(245,240,230,0.1)",
              maxHeight: "85vh",
            }}
            initial={{ y: "100%" }}
            animate={{ y: 0 }}
            exit={{ y: "100%" }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            data-testid="advanced-builder-sheet"
          >
            <div className="flex flex-col" style={{ maxHeight: "85vh" }}>
              {/* Handle */}
              <div className="flex justify-center pt-3 pb-2">
                <div className="w-10 h-1 rounded-full" style={{ background: "rgba(245,240,230,0.15)" }} />
              </div>

              {/* Header */}
              <div className="flex items-center justify-between px-6 pb-4">
                <div>
                  <p className="text-[10px] uppercase tracking-[0.3em] mb-1 font-light" style={{ color: "rgba(203,161,83,0.55)" }}>
                    Advanced Mode
                  </p>
                  <h3 className="text-xl italic" style={{ fontFamily: "'Playfair Display', serif", color: "rgba(245,240,230,0.92)", fontWeight: 300 }}>
                    Build your session
                  </h3>
                </div>
                <button onClick={onClose} style={{ color: "rgba(245,240,230,0.5)" }} data-testid="advanced-builder-close">
                  <X size={18} />
                </button>
              </div>

              {/* Tabs */}
              <div className="flex gap-2 px-6 pb-4">
                {[
                  { id: "music", label: "Music", icon: Music },
                  { id: "binaural", label: "Binaural", icon: Brain },
                  { id: "mode", label: "Mode", icon: Sparkles },
                ].map((t) => {
                  const Icon = t.icon;
                  const active = tab === t.id;
                  return (
                    <button
                      key={t.id}
                      onClick={() => setTab(t.id)}
                      className="flex-1 flex items-center justify-center gap-1.5 rounded-full py-2 text-[11px] font-light"
                      style={{
                        background: active ? "rgba(203,161,83,0.15)" : "rgba(245,240,230,0.04)",
                        border: `1px solid ${active ? "rgba(203,161,83,0.4)" : "rgba(245,240,230,0.08)"}`,
                        color: active ? "rgba(203,161,83,0.95)" : "rgba(245,240,230,0.6)",
                      }}
                      data-testid={`advanced-tab-${t.id}`}
                    >
                      <Icon size={12} strokeWidth={1.4} />
                      {t.label}
                    </button>
                  );
                })}
              </div>

              {/* Content (scrollable) */}
              <div className="flex-1 overflow-y-auto px-6 pb-8">
                {/* MUSIC TAB */}
                {tab === "music" && (
                  <div className="space-y-6" data-testid="advanced-tab-content-music">
                    {orderedVibeKeys.map((vibe) => {
                      const tracks = groupedTracks[vibe];
                      return (
                      <div key={vibe}>
                        <p className="text-[10px] uppercase tracking-[0.25em] mb-3 font-light" style={{ color: "rgba(245,240,230,0.4)" }}>
                          {VIBE_LABELS[vibe] || vibe}
                        </p>
                        <div className="space-y-2">
                          {tracks.map((tr) => {
                            const active = selection?.musicId === tr.id;
                            const isPreviewing = previewing === tr.id;
                            return (
                              <div
                                key={tr.id}
                                className="rounded-xl flex items-center gap-3 p-3 transition-all"
                                style={{
                                  background: active ? "rgba(203,161,83,0.12)" : "rgba(245,240,230,0.03)",
                                  border: `1px solid ${active ? "rgba(203,161,83,0.4)" : "rgba(245,240,230,0.06)"}`,
                                }}
                                data-testid={`advanced-track-${tr.id}`}
                              >
                                <button
                                  onClick={() => togglePreview(tr)}
                                  className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0"
                                  style={{
                                    background: isPreviewing ? "rgba(203,161,83,0.25)" : "rgba(245,240,230,0.06)",
                                    border: `1px solid ${isPreviewing ? "rgba(203,161,83,0.5)" : "rgba(245,240,230,0.1)"}`,
                                  }}
                                  data-testid={`preview-${tr.id}`}
                                >
                                  {isPreviewing ? (
                                    <Pause size={12} style={{ color: "rgba(203,161,83,0.95)" }} strokeWidth={1.4} />
                                  ) : (
                                    <Play size={12} style={{ color: "rgba(245,240,230,0.7)", marginLeft: 1 }} strokeWidth={1.4} />
                                  )}
                                </button>
                                <button
                                  onClick={() => onChange({ ...selection, musicId: tr.id })}
                                  className="flex-1 text-left"
                                  data-testid={`select-${tr.id}`}
                                >
                                  <p className="text-sm font-light" style={{ color: active ? "rgba(203,161,83,0.95)" : "rgba(245,240,230,0.85)" }}>
                                    {tr.name}
                                  </p>
                                  {isPreviewing && (
                                    <p className="text-[10px] uppercase tracking-[0.2em] mt-0.5 font-light" style={{ color: "rgba(203,161,83,0.6)" }}>
                                      Preview · 15s
                                    </p>
                                  )}
                                </button>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                      );
                    })}
                  </div>
                )}

                {/* BINAURAL TAB */}
                {tab === "binaural" && (
                  <div className="space-y-3" data-testid="advanced-tab-content-binaural">
                    <p className="text-xs italic mb-4 font-light" style={{ color: "rgba(245,240,230,0.45)", fontFamily: "'Spectral', serif" }}>
                      Brainwave entrainment — felt, not heard. Use headphones for full effect.
                    </p>
                    {Object.entries(binauralPresets).map(([id, preset]) => {
                      // Skip the smooth-progression presets (system-managed)
                      if (id.includes("_to_")) return null;
                      const active = selection?.binauralId === id;
                      return (
                        <button
                          key={id}
                          onClick={() => onChange({ ...selection, binauralId: id })}
                          className="w-full text-left rounded-xl p-4 transition-all"
                          style={{
                            background: active ? "rgba(139,146,242,0.12)" : "rgba(245,240,230,0.03)",
                            border: `1px solid ${active ? "rgba(139,146,242,0.4)" : "rgba(245,240,230,0.06)"}`,
                          }}
                          data-testid={`binaural-${id}`}
                        >
                          <div className="flex items-center justify-between">
                            <p className="text-sm font-light" style={{ color: active ? "rgba(165,180,252,0.95)" : "rgba(245,240,230,0.85)" }}>
                              {preset.label}
                            </p>
                            {preset.beat_hz > 0 && (
                              <p className="text-[10px] uppercase tracking-[0.2em] font-light" style={{ color: active ? "rgba(165,180,252,0.6)" : "rgba(245,240,230,0.35)" }}>
                                {preset.beat_hz} Hz
                              </p>
                            )}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}

                {/* MODE TAB */}
                {tab === "mode" && (
                  <div data-testid="advanced-tab-content-mode">
                    <p className="text-xs italic mb-6 font-light text-center" style={{ color: "rgba(245,240,230,0.45)", fontFamily: "'Spectral', serif" }}>
                      Choose how the voice sits in the mix.
                    </p>
                    <ModeSelector
                      selected={selection?.mode || "soft"}
                      onChange={(m) => onChange({ ...selection, mode: m })}
                    />
                  </div>
                )}
              </div>

              {/* Footer (apply) */}
              <div className="px-6 pb-6 pt-3" style={{ borderTop: "1px solid rgba(245,240,230,0.06)" }}>
                <button
                  onClick={onClose}
                  className="w-full rounded-full py-3 text-sm font-light"
                  style={{
                    background: "rgba(203,161,83,0.18)",
                    border: "1px solid rgba(203,161,83,0.4)",
                    color: "rgba(203,161,83,0.95)",
                  }}
                  data-testid="advanced-builder-apply"
                >
                  Done
                </button>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
